// Subscription utility functions and feature gates

export const SUBSCRIPTION_PLANS = {
  FREE: 'free',
  PREMIUM: 'premium',
  LIFETIME: 'lifetime',
  ENTERPRISE: 'enterprise'
};

export const PLAN_FEATURES = {
  free: {
    maxItems: 25,
    storyApproval: false,
    bulkGeneration: false,
    bulkItemCreation: false,
    maxBulkItems: 0,
    advancedAnalytics: false,
    prioritySupport: false,
    customCategories: false,
    exportData: false,
    emailNotifications: true,
    customQRDesigns: false,
    storyModeration: false,
    detailedTracking: false,
    name: 'Free Explorer',
    description: 'Perfect for trying out iWasHere.me',
    price: 0,
    monthlyPrice: 0,
    yearlyPrice: 0,
    features: [
      'Up to 25 trackable items',
      'Basic QR code generation',
      'Email notifications',
      'Public story viewing',
      'Basic journey tracking',
      'Community support'
    ]
  },
  lifetime: {
    maxItems: -1, // unlimited
    storyApproval: true,
    bulkGeneration: 200, // Double the monthly limit since it's lifetime
    bulkItemCreation: true,
    maxBulkItems: 100, // Double the monthly limit
    advancedAnalytics: true,
    prioritySupport: true,
    customCategories: true,
    exportData: true,
    emailNotifications: true,
    customQRDesigns: true,
    storyModeration: true,
    storyDeletion: true,
    detailedTracking: true,
    advancedSearch: true,
    apiAccess: true,
    customBranding: true,
    teamManagement: false,
    name: 'Lifetime Tracker',
    description: 'One-time payment, lifetime access to premium features',
    price: 49,
    monthlyPrice: 0,
    yearlyPrice: 0,
    lifetimePrice: 49,
    features: [
      '🎉 One-time payment - Lifetime access!',
      'Everything in Premium Tracker',
      'Unlimited trackable items forever',
      'Delete your own stories & manage your items',
      'Export journey PDFs with maps & stories',
      'Custom branding options',
      'Double bulk limits (200 QR codes/month)',
      'Bulk item creation (100 items/month)',
      'Priority support for life',
      'All future premium features included',
      'Perfect for organizations & power users'
    ]
  },
  premium: {
    maxItems: -1, // unlimited
    storyApproval: true,
    bulkGeneration: 100,
    bulkItemCreation: true,
    maxBulkItems: 50,
    advancedAnalytics: true,
    prioritySupport: true,
    customCategories: true,
    exportData: true,
    emailNotifications: true,
    customQRDesigns: true,
    storyModeration: true,
    storyDeletion: true,
    detailedTracking: true,
    advancedSearch: true,
    apiAccess: true,
    customBranding: false,
    teamManagement: false,
    name: 'Premium Tracker',
    description: 'Everything you need for serious tracking',
    price: 1.99,
    monthlyPrice: 1.99,
    yearlyPrice: 19.99, // 2 months free
    features: [
      'Unlimited trackable items',
      'Custom QR code designs & colors',
      'Email notifications for item updates',
      'Advanced analytics & journey insights',
      'Story approval & moderation tools',
      'Delete your own stories & manage your items',
      'Bulk QR code generation (100/month)',
      'Bulk QR Code Registration & Ownership',
      'Export journey PDFs with maps & stories',
      'Export data (CSV, JSON)',
      'Custom item categories',
      'API access for integrations',
      'Priority support',
      'Advanced search & filtering',
      'Premium features & early access'
    ]
  }
};

// Feature gate functions
export const canCreateMoreItems = (userPlan, currentItemCount) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.maxItems === -1 || currentItemCount < features.maxItems;
};

export const hasStoryApproval = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.storyApproval;
};

export const canBulkGenerate = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.bulkGeneration > 0;
};

export const getBulkGenerationLimit = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.bulkGeneration;
};

export const hasAdvancedAnalytics = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.advancedAnalytics;
};

export const canExportData = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.exportData;
};

export const canExportJourney = (userPlan) => {
  // PDF export and share functionality restored for all users
  return true;
};

export const canBulkCreateItems = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.bulkItemCreation;
};

export const getBulkItemLimit = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.maxBulkItems;
};

export const hasCustomCategories = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.customCategories;
};

export const canDeleteStories = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.storyDeletion || false;
};

export const getPlanDisplayName = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.name;
};

export const getPlanPrice = (userPlan) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  return features.price;
};

// Usage tracking
export const checkItemLimit = (userPlan, currentItemCount) => {
  const features = PLAN_FEATURES[userPlan] || PLAN_FEATURES.free;
  
  if (features.maxItems === -1) {
    return { canCreate: true, remaining: 'unlimited' };
  }
  
  const remaining = features.maxItems - currentItemCount;
  return {
    canCreate: remaining > 0,
    remaining: Math.max(0, remaining),
    limit: features.maxItems
  };
};

// Upgrade prompts
export const getUpgradeMessage = (feature) => {
  const messages = {
    items: 'Upgrade to Premium to track unlimited items and spread more kindness!',
    approval: 'Upgrade to Premium to review and approve stories before they go live.',
    bulk: 'Upgrade to Premium for bulk QR code generation and save time.',
    bulkItems: 'Upgrade to Premium to create multiple items at once - perfect for events, classrooms, and organizations!',
    analytics: 'Upgrade to Premium for detailed analytics and journey insights.',
    export: 'Upgrade to Premium to export your journey data and create reports.',
    journeyExport: 'Upgrade to Premium to export beautiful PDF journey reports with maps, stories, and photos!',
    categories: 'Upgrade to Premium for custom categories and better organization.'
  };
  
  return messages[feature] || 'Upgrade to Premium for more amazing features!';
};

export const isPremiumFeature = (feature) => {
  const premiumFeatures = [
    'storyApproval',
    'storyDeletion',
    'bulkGeneration', 
    'advancedAnalytics',
    'exportData',
    'customCategories',
    'prioritySupport'
  ];
  
  return premiumFeatures.includes(feature);
};